import { cloneElement, isValidElement } from 'react';
import type { ReactElement, ReactNode } from 'react';

export interface FormControlAriaProps {
    'aria-describedby'?: string;
    'aria-required'?: boolean;
    'aria-invalid'?: boolean;
}

interface FormFieldProps {
    label: string;
    htmlFor: string;
    required?: boolean;
    help?: string;
    error?: string;
    inline?: boolean;
    children: ReactNode;
}

// FormComponents/Dropdown and Input/TextInput render the required marker before the label and have no helper-text prop,
// which the Spiris form design needs — see the VSCO-3786 PR discussion before swapping them in
const FormField = ({ label, htmlFor, required, help, error, inline, children }: FormFieldProps) => {
    const message = error ?? help;
    const messageId = `${htmlFor}-help`;
    const ariaProps: FormControlAriaProps = {
        'aria-describedby': message === undefined ? undefined : messageId,
        'aria-required': required === true || undefined,
        'aria-invalid': error === undefined ? undefined : true,
    };
    // a component child must forward these itself (see TimeLengthFields / EnumSelect)
    // eslint-disable-next-line @eslint-react/no-clone-element -- keeps call sites as plain JSX; only the ARIA wiring is added
    const control = isValidElement(children) ? cloneElement(children as ReactElement<FormControlAriaProps>, ariaProps) : children;

    return (
        <div className={`form-group${inline === true ? ' flex-1' : ''}${error === undefined ? '' : ' has-error'}`}>
            <label htmlFor={htmlFor}>
                {label}
                {required === true && <span className="text-error"> *</span>}
            </label>
            {control}
            {message !== undefined && (
                <span id={messageId} className={`help-block font-size-xs${error === undefined ? '' : ' text-error'}`} aria-live={error === undefined ? undefined : 'polite'}>
                    {message}
                </span>
            )}
        </div>
    );
};

export default FormField;
